/*
 * Copyright (c) 2023-2025 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { defaultLayouts } from "../dashboard/DashboardUtil";
import { getAgeFromBirthday } from "../util/DateTime";
import { invalidResponse } from "./RequestUtil";
import { getLoginLocalization, validateLocale } from "../util/Localization";
import { convertPostLoginType } from "../pages/loginPage/LoginPageUtil";
import { mergeDeep } from "../util/Object";
import debug from "../util/constants/Debug";
import { hasSmsLogin } from "../pages/layouts/LayoutPortalUtil";
import { constants } from "../util/constants/Constants";

// helper variables
const invalidResponseError = { error: invalidResponse };

// helper functions
const parseAddress = (item) => {
  return {
    street: item.street,
    city: item.city,
    state: item.state,
    zip: item.zip,
    country: item.country,
    timezone: item.use_local_timezone ? null : item.timezone,
    realTimezone: item.timezone,
    useLocalTimezone: item.use_local_timezone,
  };
};

const parseCaptivePortalSchedule = (scheduleId, schedules) => {
  if (!schedules || scheduleId == null) {
    return null;
  }
  const schedule = schedules.find((schedule) => schedule.id === scheduleId);
  if (!schedule) {
    return null;
  }
  return {
    id: schedule.id,
    startDay: schedule.start_day,
    startMonth: schedule.start_month,
    endDay: schedule.end_day,
    endMonth: schedule.end_month,
    restrictWeekday: schedule.restrict_weekday,
  };
};

const parseCaptivePortalLayout = (layoutId, layouts) => {
  if (!layouts || layoutId == null) {
    return null;
  }
  return layouts.find((layout) => layout.id === layoutId); 
};

const parseCaptivePortals = (linkId, portals, {layouts, schedules}) => {
  if (!portals || linkId == null) {
    return null;
  }
  const noneActive = !portals.some((captivePortal) => captivePortal.active);
  return portals.filter((captivePortal) => captivePortal.link_id === linkId).map((captivePortal) => {
    const isDefault = captivePortal.default ? true : false;
    const isActive = captivePortal.active ? true : false;
    return {
      id: captivePortal.id,
      name: captivePortal.name,
      default: isDefault,
      active: noneActive ? isDefault : isActive,
      layout: parseCaptivePortalLayout(captivePortal.layout_id, layouts),
      schedule: parseCaptivePortalSchedule(captivePortal.schedule_id, schedules)
    }
  });
};

const parseImageUrl = (imageId, images) => {
  return images?.find((image) => image.id === imageId)?.image_url;
};

const parseHierarchyInfo = (item, {portals, layouts, schedules, images}) => {
  return {
    // contact info
    email: item.email,
    phoneNumber: item.phone_number,
    website: item.website_url,

    // address
    address: parseAddress(item),

    // captive portal
    captivePortals: parseCaptivePortals(item.captive_portal_link_id, portals, {layouts, schedules}),

    // images
    logoImageUrl: parseImageUrl(item.logo_image_id, images),
    splashScreenImageUrl: parseImageUrl(item.splash_screen_image_id, images),
    requireEmailValidation: item.req_email_validation,
    requireSmsValidation: item.req_sms_validation,
    httpsHotspotEnabled: item.https_hotspot_enabled,
    hotspotRedirectUrl: item.hotspot_redirect_url,
    routerIdleTimeout: item.router_idle_timeout ?? 0,
  };
};

const parseAccountInfo = (account) => {
  const permissions = account.permissions;
  return {
    id: account.id ?? account.user_id,
    admin: account.admin,
    subAdmin: account.sub_admin,
    firstName: account.first_name,
    lastName: account.last_name,
    userId: account.user_id,
    username: account.username,
    useLocalTimezone: account.use_local_timezone,
    timezone: account.timezone,
    isStatic: account.is_static,
    clientName: account.client_name,
    clientList: account.client_list?.map((client) => ({
      id: client.id,
      name: client.name
    })),
    brands: account.branding?.map((brand) => ({
      id: brand.id,
      title: brand.title,
      dashboardDomain: brand.dashboard_domain,
      dashboardDomainVerified: brand.dashboard_domain_verified,
      dashboardDomainError: brand.dashboard_domain_error,
      accounts: brand.accounts?.map((account) => ({
        id: account.id,
        username: account.username
      }))
    })),
    permissions: permissions ? {
      editTemplates: permissions.edit_templates,
      editLayouts: permissions.edit_layouts,
      provisionBusiness: permissions.provision_business,
      readOnly: permissions.read_only
    } : {}
  };
};

const parseClient = (client) => {
  const locations = client.locations ?? [];
  const routers = client.nas_client_info ?? [];
  const accounts = client.accounts ?? [];
  const images = client.images ?? [];
  const captivePortals = client.captive_portals ?? [];
  const captivePortalLayouts = client.captive_portal_layouts ?? [];
  const captivePortalSchedules = client.captive_portal_schedules ?? [];
  const brand = client.branding ?? null;

  const hierarchyData = {
    portals: captivePortals,
    layouts: captivePortalLayouts,
    schedules: captivePortalSchedules,
    images: images
  };

  const billingStatus = client.billing_status;
  const statusString = billingStatus ? billingStatus.charAt().toUpperCase() + billingStatus.slice(1).toLowerCase() : null;

  return {
    // client info
    ...parseHierarchyInfo(client, hierarchyData),
    id: client.id,
    name: client.name,
    localization: client.language,
    productAndServices: client.product_and_services,
    taxId: client.vat_tax_id,
    test: client.test,
    brandId: client.brand_id,
    brandStatus: client.brand_status,
    brandError: client.brand_error,
    brandIdPending: client.brand_id_pending,
    brandTitlePending: client.brand_title_pending,

    // billing info
    billing: {
      status: statusString,
      name: client.billing_subscription_name,
      type: client.billing_subscription_type,
      quantity: client.billing_subscription_quantity,
      planId: client.billing_plan_id,
      subscriptionId: client.billing_subscription_id,
      start: client.billing_start,
      lastPayment: client.billing_last_payment,
      nextPayment: client.billing_next_payment,
      invoiceSent: client.billing_invoice_sent,
      pendingPlanId: client.billing_pending_plan_id,
      pendingPlanType: client.billing_pending_plan_type,
      pendingPlanName: client.billing_pending_plan_name,
      pendingQuantity: client.billing_pending_subscription_quantity,
      billingLocations: client.billing_locations,
      pendingInvoiceId: client.billing_pending_invoice_id,
      recurringInvoice: client.billing_recurring_invoice,
      invoiceScheduledAt: client.billing_invoice_scheduled_at,
      invoiceScheduledFor: client.billing_invoice_scheduled_for,
      invoiceDiscountValue: client.billing_invoice_discount_value,
      invoiceDiscountType: client.billing_invoice_discount_type,
    },
    brand: brand ? parseBrandItem(brand) : null,
    hasSmsConfig: client.has_sms_config,

    // location info
    locations: locations.map((location) => ({
      ...parseHierarchyInfo(location, hierarchyData),
      id: location.id,
      clientId: client.id,
      name: location.name,
      description: location.description,
      googleReviewSearch: location.google_review_search,
      
      // billing info
      billing: {
        active: location.billing_active,
        start: location.billing_start,
      },

      // router info
      routers: routers.filter((router) => location.id === router.location_id).map((router) => ({
        ...parseHierarchyInfo(router, hierarchyData),
        id: router.id,
        locationId: location.id,
        locationName: location.name,
        clientId: client.id,
        clientName: client.name,
        name: router.name,
        description: router.description,
        macAddress: router.mac_address,
        ipAddress: router.ipAddress,
        vpnIp: router.vpn_ip,
        vpnBackupIp: router.vpn_backup_ip,
        guestSessionTimeout: router.guest_session_timeout,
        isOnline: router.is_configured ? router.is_online : null,
        brandStatus: router.brand_status,
        brandError: router.brand_error,
      })),
    })),

    // account info
    accounts: accounts.map((account) => parseAccountInfo(account))
  };
};

const parseAdminChart = (chartData) => ({
  timezone: chartData.timezone,
  version: chartData.version,
  updatedAt: chartData.updated_at,
  chartList: chartData.chart_list?.map((chart) => ({
    type: chart.type,
    name: chart.name,
    data: chart.data.map((item) => ({
      name: item.name,
      active: item.guest_activity,
      created: item.clients_created
    }))
  })) ?? []
});

const parseAdminAttention = (attentionData) => ({
  timezone: attentionData.timezone,
  updatedAt: attentionData.updated_at,
  attentionList: attentionData.needs_attention?.filter((item) => item.value < attentionData.updated_at)?.map((item) => ({
    level: item.attention_level,
    id: item.id,
    name: item.name,
    type: item.type,
    value: item.value
  })) ?? []
});

// parser functions
export const parseExistsResponse = ({response}) => {
  // validate response
  const exists = response.exists;
  if (exists == null) {
    return invalidResponseError;
  }

  // set temp state
  return { temp: { exists: exists } };
};

export const parseIdResponse = ({response}) => {
  // validate response
  const id = response.id;
  if (!id) {
    return invalidResponseError;
  }

  // set temp state
  return { temp: { id: id } };
};

export const parseNetworkLoginPageResponse = ({response, forcedLocale}) => {
  // validate response
  const captivePortal = response.captive_portal;
  const guestCache = response.guest_cache ?? {};
  if (captivePortal?.layout_config == null) {
    return invalidResponseError;
  }

  // set localization
  const preferredLocale = guestCache?.language;
  const defaultLocale = validateLocale(navigator.language || navigator.userLanguage);
  const loginPageLocale = captivePortal?.layout_language ?? "";

  const locale = [forcedLocale, preferredLocale, defaultLocale, loginPageLocale, "en"].reduce((acc, item) => {
    return acc ? acc : item;
  }, null);
  getLoginLocalization().setLanguage(locale);

  // helpers
  const respLoginUser = guestCache.login_user ?? {};
  const loginUser = {     
    name: respLoginUser.first_name,
    email: respLoginUser.email,
    username: respLoginUser.username,
  };
  loginUser.display = loginUser.name ?? loginUser.email;

  // set account state
  return {
    captivePortal: {
      config: {
        layout: captivePortal.layout_config,
        fields: {
          businessName: captivePortal.business_name,
          locationName: captivePortal.location_name,
          routerName: captivePortal.router_name,
          email: captivePortal.email,
          website: captivePortal.website_url,
          logoUrl: captivePortal.logo_image_url,
          splashScreenUrl: captivePortal.splash_screen_image_url,
          splashScreenEnabled: !!captivePortal.splash_screen_enabled,
          httpsHotspotEnabled: captivePortal.https_hotspot_enabled,
          hotspotRedirectUrl: captivePortal.hotspot_redirect_url,
          routerIdleTimeout: captivePortal.router_idle_timeout,
          phoneNumberCountryCode: captivePortal.phone_number_country_code,
          country: captivePortal.country,
        },
      }
    },
    login: {
      sessionExpired: response.session_expired,
      loginToken: guestCache.login_token,
      loginUser: loginUser,
      localization: locale,
      tosHtml: captivePortal.layout_tos_config?.custom,
      tosTextColor: captivePortal.layout_tos_config?.textColor,
      tosButtonBackgroundColor: captivePortal.layout_tos_config?.buttonBackgroundColor,
      tosButtonTextColor: captivePortal.layout_tos_config?.buttonTextColor,
      tosAccepted: guestCache.tos_accepted ?? false,
      gdprEnabled: captivePortal.force_gdpr || captivePortal.layout_gdpr_config?.enabled,
      gdprFormEnabled: captivePortal.force_gdpr || captivePortal.layout_gdpr_config?.formEnabled,
      gdprForm: captivePortal.layout_gdpr_config?.form,
      gdprAccepted: guestCache.gdpr_accepted,
      hasSmsConfig: captivePortal.has_sms_config,
      hasFacebookConfig: captivePortal.has_facebook_config,
      hasGoogleConfig: captivePortal.has_google_config,
      hasTwitterConfig: captivePortal.has_twitter_config,
      facebookClientId: captivePortal.facebook_client_id,
      googleClientId: captivePortal.google_client_id,
      customDomain: captivePortal.custom_domain,
    }
  };
};

export const parseNetworkVerifyConnect = ({response}) => {
  const loginToken = response.login_token;
  const accountState = response.account_status_id;
  const verificationRequired = accountState === "EMAIL_NOT_CONFIRMED";

  if (!loginToken && !accountState) {
    return invalidResponseError;
  }

  return {
    login: {
      connectLoginToken: loginToken,
      verificationRequired: verificationRequired
    }
  };
};

export const parseNetworkCodeVerify = ({response}) => {
  // TODO: Parse out error message response (key 'message')

  return {
    login: {
      connectLoginToken: response.login_token,
    }
  };
};

export const parseNetworkVerifyResponse = ({response}) => {
  // validate response
  const accountState = response.account_status_id;
  let verificationRequired = null;

  switch (accountState) {
    case "EMAIL_CONFIRMED":
      verificationRequired = false;
      break;
    case "EMAIL_NOT_CONFIRMED":
      verificationRequired = true;
      break;
    default:
      // do nothing
      break;
  }

  // set login state
  return { login: { verificationRequired, email: response.email }};
};

export const parseNetworkSmsVerifyResponse = ({response}) => {
  // validate response
  const registrationRequired = response.message === "No user found";
  const passwordSent = response.password_expires_in !== null && response.password_expires_in !== undefined;
  const message = response.message;
  // set login state
  return { login: { registrationRequired, passwordSent, message }};
};

export const parseDashboardLoginResponse = ({response}) => {
  // validate login info
  const isAdmin = response?.admin ?? false;
  const isSubAdmin = response?.sub_admin ?? false;
  const token = response?.token;
  const clientId = response?.client_id;
  const permissions = response?.permissions;
  const domainBrand = response?.domain_brand;

  if ((token == null) ||                             // user token required
    (!isAdmin && !isSubAdmin && clientId == null))   // non-admins must be associated with client id
  {
    return invalidResponseError;
  }

  // save context state and navigate to new page
  return {
    account: {
      id: response.user_id ?? response.id,
      admin: isAdmin,
      subAdmin: isSubAdmin,
      token: token,
      clientId: isAdmin ? null : clientId,
      username: response?.username,
      permissions: permissions ? {
        editTemplates: permissions.edit_templates,
        editLayouts: permissions.edit_layouts,
        provisionBusiness: permissions.provision_business,
        readOnly: permissions.read_only,
      } : {},
      domainBrand: domainBrand ? parseBrandItem(domainBrand) : null,
    }
  };
};

export const parseAdminDataResponse = ({response}) => {  
  // response to context keys
  const responseKeys = {
    adminCaptivePortalLayoutList: "captive_portal_list",
    adminClientList: "client_list",
    adminLocationList: "location_list",
    adminRouterList: "router_list",
    adminBroadcastList: "broadcast_list",
    adminAutomationList: "automations_list",
    adminAccountList: "account_list",
    adminTemplateList: "template_list",
    adminBrandList: "branding",
  }

  // check each list to verify non-null
  const valid = Object.values(responseKeys).reduce((acc, item) => acc && response[item] != null, true);
  if (!valid) {
    debug.warn("Could not parse admin dashboard response:");
    debug.warn(response);
    return invalidResponse;
  }

  // parse out lists
  const account = {};
  const currentTimestamp = Math.round((new Date()).getTime() / 1000);

  Object.keys(responseKeys).forEach(key => {
    const isClient = key === "adminClientList";
    const isLocation = key === "adminLocationList";
    const isRouter = key === "adminRouterList";
    const isBroadcast = key === "adminBroadcastList";
    const isAutomation = key === "adminAutomationList";
    const isAccount = key === "adminAccountList";
    const isHierarchy = isClient || isLocation || isRouter;
    const isMarketing = isBroadcast || isAutomation;
    const isBranding = key === "adminBrandList";
    account[key] = response[responseKeys[key]].map((item) => ({
      id: item.id,
      name: item.name,
      createdAt: item.created_at,
      ...(!isClient) && { clientId: item.client_id, clientName: item.client_name },
      ...(isHierarchy) && { lastActive: currentTimestamp - (item.last_guest_activity ?? item.created_at) },
      ...(isRouter) && { macAddress: item.mac_address, isOnline: item.is_online },
      ...(isMarketing) && { medium: item.type },
      ...(isAccount) && { name: item.name, email: item.email },
      ...(isBranding) && { id: item.id, title: item.title },
      ...(isClient) && { brandTitle: item.brand_title },
    }))
  });

  // marketing list
  account.adminMarketingList = [
    ...account.adminBroadcastList.map(broadcast => ({ ...broadcast, marketingType: "broadcast" })),
    ...account.adminAutomationList.map(automation => ({ ...automation, marketingType: "automation" }))
  ].sort((a, b) => b.createdAt - a.createdAt);

  // add chart and attention data
  const chartData = parseAdminChart(response.chart_list);
  account.chartData = chartData;
  account.chartScope = chartData.chartList[0]?.type;

  account.attention = parseAdminAttention(response.needs_attention);

  // set loaded
  account.adminLoaded = true;

  // Set brand list
  account.brands = response?.branding?.map((item) => parseBrandItem(item))

  return { account };
};

export const parseAdminChartResponse = ({response}) => {
  const chartData = parseAdminChart(response.chart_list);
  return {
    account: {
      chartData: parseAdminChart(response),
      chartScope: chartData.chartList[0]?.type
    }
  }
};

export const parseAdminAttentionResponse = ({response}) => {
  return {
    account: {
      attention: parseAdminAttention(response.needs_attention)
    }
  };
};

export const parseClientResponse = ({response, contextKey = "account", key = "clientInfo", settingsPage }) => {
  try {
    const output = parseClient(response.client);

    // settings page
    const inKeyAttr = {};
    const outKeyAttr = {};

    if (settingsPage != null) {
      if (contextKey === "account") {
        inKeyAttr.settingsPage = settingsPage;
      } else {
        outKeyAttr.settingsPage = settingsPage;
      }
    }

    // return state
    return {
      [contextKey]: {
        [key]: {
          ...output,
        },
        ...inKeyAttr,
      },
      ...outKeyAttr
    }
  } catch (e) {
    debug.warn("Could not parse client response: " + e);
    return invalidResponseError
  }
}

export const parseAllClientResponse = ({response}) => {
  try {
    const output = response.filter((client) => client.name !== "admin").map((client) => parseClient(client));
    
    // return state
    return {
      account: {
        clientList: output
      }
    }
  } catch (e) {
    debug.warn("Could not parse client response: " + e);
    return invalidResponseError
  }
};

export const parseClientActivityResponse = ({response, currentActivities}) => {
  // validate response
  const activityList = response.activity;
  if (!activityList || !Array.isArray(activityList)) {
    return invalidResponseError;
  }

  // parse activities
  const newList = activityList.map(activity => ({
    id: activity.id,
    type: activity.event_type,
    clientId: activity.client_id,
    locationId: activity.location_id,
    routerId: activity.router_id,
    timestamp: activity.timestamp,
    routerMacAddress: activity.nas_client_mac_address,
    guestName: activity.guest_name,
    emailName: activity.email_name,
    deviceMacAddress: activity.device_mac_address,
    guestType: activity.guest_type,
    marketing: activity.marketing,
  }));

  return {
    account: {
      activityList: currentActivities ? [...newList, ...currentActivities] : newList
    }
  };
};

export const parseCaptivePortalResponse = ({response, contextKey = "captivePortal", key = "config"}) => {
  // validate response
  const captivePortal = response.captive_portal;
  if (captivePortal?.layout_config == null) {
    return invalidResponseError;
  }

  // set state
  return {
    [contextKey]: {
      [key]: {
        id: captivePortal.layout_id,
        layout: captivePortal.layout_config,
        fields: {
          businessName: captivePortal.business_name,
          locationName: captivePortal.location_name,
          routerName: captivePortal.router_name,
          email: captivePortal.email,
          website: captivePortal.website_url,
          logoUrl: captivePortal.logo_image_url,
          splashScreenUrl: captivePortal.splash_screen_image_url
        }
      }
    }
  };
};

export const parseCaptivePortalGeneratedName = ({response}) => {
  const name = response?.name;
  if (!name) {
    return invalidResponseError;
  }

  return { createLoginPage: { suggestedName: name } };
};

export const parseCaptivePortalLayoutResponse = ({response, contextKey = "captivePortal", key = "layout"}) => {
  // validate response
  const layout = response.layout;
  const id = layout?.id;
  const config = layout?.config;

  if (id == null || config == null) {
    return invalidResponseError;
  }

  const isDefault = layout.default ?? false;
  const name = layout.name;
  const localization = layout.language;
  const tos = layout.tos_config;
  const gdpr = layout.gdpr_config;
  const forceGdpr = layout.force_gdpr;
  const postLoginConfig = layout.post_login_config ?? {};

  const splashScreen = {
    id: layout.splash_screen_image_id,
    url: layout.splash_screen_image_url,
    size: layout.splash_screen_size,
    name: layout.splash_screen_name,
    enabled: !!layout.splash_screen_enabled,
  };

  const verify = {
    email: !!layout.req_email_validation,
    sms: !!layout.req_sms_validation,
  };

  const postLogin = {
    type: convertPostLoginType(postLoginConfig.type),
    facebookUrl: postLoginConfig.facebook_like_url,
    instagramProfile: postLoginConfig.instagram_profile,
    default: postLoginConfig.default_html,
    videoUrl: postLoginConfig.embedded_video_url,
    html: postLoginConfig.html
  };

  const redirect = {
    enabled: postLoginConfig.enable_redirect,
    delay: postLoginConfig.redirect_delay,
    redirectUrl: postLoginConfig.redirect_url
  };

  // set account state
  const data = { id, default: isDefault, name, config, localization, tos, verify, gdpr, forceGdpr, postLogin, redirect, splashScreen };
  return { [contextKey]: { [key]: data } };
};

export const parseCaptivePortalAllLayoutResponse = ({response, contextKey = "captivePortal"}) => {
  // validate response
  const layouts = response.layouts;
  if (layouts == null || !Array.isArray(layouts)) {
    return invalidResponseError;
  }

  // set captive portal state
  return {
    [contextKey]: {
      layoutList: layouts.map((item) => ({
        id: item.id,
        default: item.default,
        name: item.name,
        config: item.config,
        global: item.global
      }))
    }
  };
};

export const parseCaptivePortalLayoutDeleteResponse = ({response}) => {
  // validate response 
  const affectedItems = response.affected_items?.clients;
  if (affectedItems == null) {
    return invalidResponseError;
  }

  // set captive portal delete state
  return {
    captivePortal: {
      deletePending: affectedItems ? {
        items: affectedItems.map((item) => ({
          id: item.id,
          name: item.name
        }))
      } : null
    }
  };
};

export const parseCaptivePortalAllImageResponse = ({response, contextKey = "captivePortal"}) => {
  // validate response
  const images = response.captive_portal_images;
  if (images == null || !Array.isArray(images)) {
    return invalidResponseError;
  }

  // set captive portal state
  return {
    [contextKey]: {
      imageList: images.map((image) => ({
        id: image.id,
        url: image.image_url,
        name: image.name,
        type: image.type,
      }))
    }
  }
};

export const parseSaveCaptivePortalLayoutResponse = ({response}) => {
  // validate response
  const layoutId = response.layout_id ?? response.layout?.id;
  if (layoutId == null) {
    return invalidResponseError;
  }

  // set captive portal state
  return { captivePortal: { layoutId } };
};

export const parseRouterFileResponse = ({response, key}) => {
  // save raw response
  return { captivePortal: { [key]: response } };
};

export const parseAccountResponse = ({response}) => {
  const account = response.account;
  if (!account) {
    return invalidResponseError;
  }
  return {
    account: parseAccountInfo(response.account)
  };
};

export const parsePostLoginPreviewResponse = ({response, type}) => {
  if (typeof response !== 'string') {
    return invalidResponse;
  }
  return {
    createLoginPage: {
      postLoginPreview: response,
      postLoginPreviewType: type
    }
  };
};

export const parseAllAccountsResponse = ({response}) => {
  // validate response
  const accounts = response.accounts;
  if (accounts == null || !Array.isArray(accounts)) {
    return invalidResponseError;
  }

  // parse response
  const accountList = accounts.map((account) => parseAccountInfo(account));

  // set account state
  return { account: { accountList } };
};

export const parseClientUsersResponse = ({response}) => {
  // validate response
  const guestList = response.guest_list;
  const locationList = response.location_list;

  if (!Array.isArray(guestList)) {
    return invalidResponseError;
  }

  // parse response
  const parseIsOnline = (isOnline) => {
    if (isOnline == null) {
      return "unknown";
    }
    return isOnline ? "online" : "offline";
  };

  return {
    account: {
      userList: guestList.map((user) => ({
        id: user.id,
        username: user.username,
        type: user.type,
        name: user.name,
        email: user.email,
        gender: user.gender,
        phoneNumber: user.phone_number,
        age: user.age ?? getAgeFromBirthday(user.birthday),
        birthday: user.birthday,
        optIn: user.optIn,
        marketingEmailOptIn: user.marketing_email_opt_in === 1,
        marketingSmsOptIn: user.marketing_sms_opt_in === 1,
        marketingClientSmsOptIn: user.marketing_client_sms_opt_in === 1,
        lastLogin: user.last_login,
        loyaltyStatus: user.loyalty_status,
        loyaltyScore: user.loyalty_score,
        isOnline: user.is_online,
        isOnlineStatus: parseIsOnline(user.is_online),
        locationList: user.locations,
        visits: user.visits,
        gdprAccepted: user.gdpr_accepted,
        country: user.country,
        locale: user.locale,
        customTextField: user.custom_text_field,
        language: user.language,
        userLanguage: user.user_language,
        timezone: user.timezone,
      })),
      userLocationList: locationList?.map((location) => ({
        id: location.id,
        name: location.name,
      }))
    }
  };
};

export const parseGuestProfileResponse = ({response}) => {
  // validate response
  const guest = response.guest;

  if (guest == null) {
    return invalidResponseError;
  }

  // parse response
  const parseIsOnline = (isOnline) => {
    if (isOnline == null) {
      return "unknown";
    }
    return isOnline ? "online" : "offline";
  };


  const guestProfile = {
    id: guest.id,
    username: guest.username,
    type: guest.type,
    name: guest.name,
    email: guest.email,
    gender: guest.gender,
    phoneNumber: guest.phone_number,
    age: guest.age ?? getAgeFromBirthday(guest.birthday),
    optIn: guest.optIn,
    marketingEmailOptIn: guest.marketing_email_opt_in === 1,
    marketingSmsOptIn: guest.marketing_sms_opt_in === 1,
    marketingClientSmsOptIn: guest.marketing_client_sms_opt_in === 1,
    lastLogin: guest.last_login,
    loyaltyStatus: guest.loyalty_status,
    loyaltyScore: guest.loyalty_score,
    isOnline: guest.is_online,
    isOnlineStatus: parseIsOnline(guest.is_online),
    locationList: guest.locations,
    visits: guest.visits,
    gdprAccepted: guest.gdpr_accepted,
    guestDevice: guest.guest_device,
    language: guest.language,
    userLanguage: guest.user_language,
    customTextField: guest.custom_text_field,

    // Visits list
    visitList: guest.visit_list?.map((item) => {
      return {
        clientId: item.client_id,
        eventType: item.event_type,
        guestId: item.user_id,
        name: item.name,
        email: item.email,
        macAddress: item.macAddress,
        loyaltyStatus: item.loyalty_status,
        sessionTime: item.session_time,
        bytesIn: item.bytes_in,
        bytesOut: item.bytes_out,
        identity: item.nas_Client_identity,
        createdAt: item.created_at,
      }
    }),
    automationHistory: guest.automation_history?.map(item => {
      return {
        id: item.id,
        eventType: item.event_type,
        automationId: item.automation_id,
        automationName: item.automation_name,
        automationType: item.automation_type,
        triggerType: item.trigger_type,
        guestId: item.guest_id,
        createdAt: item.created_at
      }
    }),
    broadcastHistory: guest.broadcast_history?.map(item => {
      return {
        id: item.id,
        eventType: item.event_type,
        broadcastId: item.broadcast_id,
        broadcastName: item.broadcast_name,
        broadcastType: item.broadcast_type,
        guestId: item.guest_id,
        createdAt: item.created_at
      }
    }),
    marketing: guest.marketing,
    averageVisitDuration: guest.average_visit_duration,
    totalVisitDuration: guest.total_visit_duration,
    emailsSent: guest.emails_sent,
    emailsOpened: guest.emails_opened,
    emailsClicked: guest.emails_clicked,
    smsSent: guest.sms_sent,
    smsClicked: guest.sms_clicked,
  }

  return {
    account: {
      guestProfile: guestProfile
    }
  };
};

export const parseFilterMarketingBroadcastClientGuestsResponse = ({response}) => {
  const users = response.users;
  // validate response
  if (!Array.isArray(users)) {
    return invalidResponseError;
  }

  // parse response
  const parseLoginType = (type) => {
    switch (type) {
      case "email": return "Email";
      case "google": return "Google";
      case "facebook": return "Facebook";
      case "apple": return "Apple";
      case "linked_in": return "LinkedIn";
      case "twitter": return "Twitter";
      case "sms": return "SMS";
      default: return null;
    }
  };

  const parseLoyaltyStatus = (loyalty) => {
    switch (loyalty) {
      case "new": return "New";
      case "returning": return "Returning";
      case "loyal": return "Loyal";
      default: return null;
    }
  };

  const parseIsOnline = (isOnline) => {
    if (isOnline == null) {
      return "Unknown";
    }
    return isOnline ? "Online" : "Offline";
  };

  return {
    account: {
      marketingBroadcastUsers: users.map((user) => ({
        id: user.id,
        username: user.username,
        type: parseLoginType(user.type),
        name: user.name,
        email: user.email,
        gender: user.gender,
        phoneNumber: user.phone_number,
        age: user.age ?? getAgeFromBirthday(user.birthday),
        visitCount: user.visits,
        language: user.language ?? user.user_language,
        customTextField: user.custom_text_field,
        optIn: user.optIn,
        marketingEmailOptIn: user.marketing_email_opt_in === 1,
        marketingSmsOptIn: user.marketing_sms_opt_in === 1,
        marketingClientSmsOptIn: user.marketing_client_sms_opt_in === 1,
        lastLogin: user.last_login,
        loyaltyStatus: parseLoyaltyStatus(user.loyalty_status),
        loyaltyScore: user.loyalty_score,
        isOnline: user.is_online,
        isOnlineStatus: parseIsOnline(user.is_online),
      }))
    }
  };
};

export const parseTemplateResponse = ({response, settingsPage}) => {
  // validate response
  const layouts = response.layouts;
  if (layouts != null && !Array.isArray(layouts)) {
    return invalidResponseError;
  }

  // set dashboard state
  const newLayouts = response.layouts == null || response.layouts.length === 0 ?
    defaultLayouts : response.layouts

  // settings page
  const settingsPageAttr = {};
  if (settingsPage != null) {
    settingsPageAttr.settingsPage = settingsPage;
  }

  const dashboardData = {
    layouts: newLayouts,
    layoutIndex: 0,
  };
  
  return {
    dashboard: dashboardData,
    dashboardNavbar: dashboardData,
    account: settingsPageAttr
  };
};

export const parseClientStatsResponse = ({response, locationId = ""}) => {
  const charts = response.charts;
  if (charts == null) {
    return invalidResponseError;
  }

  return { dashboard: { ...charts, locationScope: locationId }};
};

export const parseAllRouterStatusesResponse = ({response, clearSelectedRouter = false}) => {
  //validate response
  const routers = response.routers
  if (!Array.isArray(routers)) {
    return invalidResponseError;
  }

  // parse response
  const routerList = routers.map((item) => ({
    id: item.id,
    name: item.name,
    clientId: item.client_id,
    clientName: item.client_name,
    locationName: item.location_name,
    nasClientMacAddress: item.mac_address,
    description: item.description,
    lastActive: item.last_active,
    lastActiveBackup: item.last_active_backup,
    notReachable: item.not_reachable,
    timestamp: item.timestamp,
    vpnIp: item.vpn_ip,
    vpnBackupIp: item.vpn_backup_ip,
    brandId: item.brand_id,
    brandTitle: item.brand_title,
  }))

  return {
    router: {
      routerList: routerList,
      ...(clearSelectedRouter) && {router: null}
    }
  };
};

export const parseRouterScriptFileResponse = ({response, key}) => {
  // save raw response
  return { router: { [key]: response } };
};

export const parseServerFileResponse = ({response, key}) => {
  // save raw response
  return { server: { [key]: response } };
};

export const parseRouterConfigurationResponse = ({response}) => {
  const interfaces = response.interfaces
  const status = response.router
  const users = response.users
  const profiles = response.profiles
  const schedules = response.schedules
  const timezone = response.timezone
  const hotspots = response.hotspots
  const channelAutoOptimizerConfig = response.channel_auto_optimizer_config
  const dhcpNetworks = response.dhcp_networks
  const hotspotProfiles = response.hotspot_profiles
  if (status == null) {
    return invalidResponseError
  }

  const interfaceList = interfaces?.map((item) => ({
    id: item.id,
    name: item.name,
    ssid: item.ssid,
    frequency: item.frequency,
    channelWidth: item.channel_width,
    masterInterface: item.master_interface,
    virtual: item.virtual,
    bridgePort: item.bridge_port,
    hotspotInterface: item.hotspot_interface,
    channelBand: item.channel_band,
  }));

  const userList = users?.map((item) => ({
    id: item.id,
    user: item.user,
    address: item.address,
    email: item.email,
    type: item.type,
  }));

  const hotspotUserProfileList = profiles?.map((item) => ({
    id: item.id,
    name: item.name,
    idleTimeout: item.idle_timeout,
    keepaliveTimeout: item.keepalive_timeout,
    rateLimit: item.rate_limit,
  }))

  const hotspotList = hotspots?.map((item) => ({
    id: item.id,
    name: item.name,
    interface: item.interface,
    profile: item.profile,
    idleTimeout: item.idle_timeout,
    keepaliveTimeout: item.keepalive_timeout,
    loginTimeout: item.login_timeout,
    https: item.https,
    disabled: item.disabled,
    enabled: item.disabled != null ? !item.disabled : item.disabled
  }))

  const schedulesList = schedules?.map((item) => ({
    id: item.id,
    name: item.name,
    startDate: item.start_date,
    startTime: item.start_time,
    interval: item.interval,
    onEvent: item.on_event,
    runCount: item.run_count,
    timezone: item.timezone,
    start: item.start
  }))

  const channelAutoOptimizerConfigItem = channelAutoOptimizerConfig ? { 
    id: channelAutoOptimizerConfig?.id,
    clientId: channelAutoOptimizerConfig?.clientId,
    startTime: channelAutoOptimizerConfig?.start_time,
    interval: channelAutoOptimizerConfig?.interval,
    lastSyncAt: channelAutoOptimizerConfig?.last_sync_at
  } : null;

  const dhcpNetworksItem = dhcpNetworks?.map((item) => ({
    id: item.id,
    address: item.address,
    servers: item.servers,
    dnsServer: item.dns_server,
    type: item.type,
    contentType: item.content_type,
  }));

  const hotspotProfilesItem = hotspotProfiles?.map((item) => ({
    id: item.id,
    name: item.name,
    dnsName: item.dns_name,
    httpsHotspotEnabled: item.https_hotspot_enabled,
  }))
  
  const routerStatus = {
    id: status.id,
    name: status.name,
    clientId: status.client_id,
    clientName: status.client_name,
    locationId: status.location_id,
    locationName: status.location_name,
    nasClientMacAddress: status.mac_address,
    description: status.description,
    lastActive: status.last_active,
    lastActiveBackup: status.last_active_backup,
    notReachable: status.not_reachable,
    timestamp: status.timestamp,
    vpnIp: status.vpn_ip,
    vpnBackupIp: status.vpn_backup_ip,
    vpnConfigName: status.vpn_config_name,
    remoteUsername: status.system_username,
    remotePassword: status.system_password,
    ssid: status.ssid,
    defaultSsid: status.default_ssid,
    routerSsid: status.router_ssid,
    interfaces: interfaceList,
    users: userList,
    profiles: hotspotUserProfileList,
    schedules: schedulesList,
    timezone: timezone,
    hotspots: hotspotList,
    enableChannelAutoOptimizer: status.enable_channel_auto_optimizer === 1,
    channelAutoOptimizedAt: status.channel_auto_optimized_at,
    guestSessionTimeout: status.guest_session_timeout,
    brandStatus: status.brand_status,
    brandError: status.brand_error,
    brandTitle: status.brand_title,
    dhcpNetworks: dhcpNetworksItem,
    hotspotProfiles: hotspotProfilesItem,
  };

  return { router: { router: routerStatus, channelAutoOptimizerConfig: channelAutoOptimizerConfigItem } };
}

export const parseRouterHttpsHotspotEnabledUpdateResponse = ({response}) => {
  const routerResponse = parseRouterConfigurationResponse({response});
  const clientResponse =  parseClientResponse({response: response,  contextKey: "router", key: "clientInfo" });
  const result = { router: { ...routerResponse.router , ...clientResponse.router }};
  return result;
}

export const parseRemoteManagerRouterResponse = ({response}) => {
  const routerResponse = parseRouterConfigurationResponse({response});
  const clientResponse =  parseClientResponse({response: response,  contextKey: "router", key: "clientInfo" });
  const result = { router: { ...routerResponse.router , ...clientResponse.router }};
  return result;
}

export const parseChannelAutoOptimizationResponse = ({response}) => {
  const configuration = response.configuration;

  if (configuration == null) { 
    return invalidResponseError;
  }

  const channelAutoOptimizerConfigItem = configuration ? { 
    id: configuration?.id,
    clientId: configuration?.clientId,
    startTime: configuration?.start_time,
    interval: configuration?.interval,
    lastSyncAt: configuration?.last_sync_at
  } : null;

  return { router: { channelAutoOptimizerConfig: channelAutoOptimizerConfigItem }}
}

export const parseRouterDisconnectUserResponse = ({response}) => {
  //TODO: Update to pull all config items
  const status = response.router
  const users = response.users
  if (status == null || users == null || !Array.isArray(users)) {
    return invalidResponseError
  }

  const userList = users.map((item) => ({
      id: item.id,
      user: item.user,
      address: item.address
  }));

  const routerStatus = {
    id: status.id,
    name: status.name,
    clientId: status.client_id,
    clientName: status.client_name,
    nasClientMacAddress: status.mac_address,
    description: status.description,
    lastActive: status.last_active,
    lastActiveBackup: status.last_active_backup,
    notReachable: status.not_reachable,
    timestamp: status.timestamp,
    vpnIp: status.vpn_ip,
    vpnBackupIp: status.vpn_backup_ip,
    users: userList
  }

  return { router: { router: routerStatus }}
}

export const parseSendgridConfigurations = ({response}) => {
  const configurations = response.sendgrid_configs

  if (configurations == null || !Array.isArray(configurations)) {
    return invalidResponseError;
  }

  const configurationList = configurations.map((item) => ({
    id: item.id,
    clientId: item.client_id,
    username: item.username,
    apiKey: item.api_key,
    password: item.password,
    fromEmail: item.from_email,
    fromName: item.from_name,
    unsubscribeGroupId: item.unsubscribe_group_id,
  }));

  return { account: { sendgridConfigurations: configurationList }};
}

export const parseTwilioSmsConfigurations = ({response}) => {
  const configurations = response.configurations

  if (configurations == null || !Array.isArray(configurations)) {
    return invalidResponseError;
  }

  const twilioSmsSupported = response.supported;

  const configurationList = configurations.map((item) => ({
    id: item.id,
    clientId: item.client_id,
    phoneNumber: item.phone_number,
    accountSid: item.account_sid,
    authToken: item.auth_token,
  }));
  
  return { account: { twilioSmsConfigurations: configurationList, twilioSmsSupported: twilioSmsSupported }};
}

export const parsePlivoSmsConfigurations = ({response}) => {
  const configurations = response.configurations

  if (configurations == null || !Array.isArray(configurations)) {
    return invalidResponseError;
  }

  const plivoSmsSupported = response.supported;

  const configurationList = configurations.map((item) => ({
    id: item.id,
    clientId: item.client_id,
    phoneNumber: item.phone_number,
    authId: item.auth_id,
    authToken: item.auth_token,
  }));
  
  return { account: { plivoSmsConfigurations: configurationList, plivoSmsSupported: plivoSmsSupported }};
}

export const parseTurboSmsConfigurations = ({response}) => {
  const configurations = response.configurations
  const turboSmsSupported = response.supported;

  if (configurations == null || !Array.isArray(configurations)) {
    return invalidResponseError;
  }

  const configurationList = configurations.map((item) => ({
    id: item.id,
    clientId: item.client_id,
    phoneNumber: item.phone_number,
    authToken: item.auth_token,
  }));
  
  return { account: { turboSmsConfigurations: configurationList, turboSmsSupported: turboSmsSupported }};
}

export const parseSendgridSenders = ({response}) => {
  const senders = response.senders
  const groups = response.unsubscribe_groups

  if (senders == null || !Array.isArray(senders)) {
    return invalidResponseError;
  }

  const sendersList = senders.map((item) => ({
    id: item.id,
    nickname: item.nickname,
    fromEmail: item.from_email,
    fromName: item.from_name,
    replyTo: item.reply_to,
    replyToName: item.reply_to_name,
    address: item.address,
    address2: item.address2,
    state: item.state,
    city: item.city,
    country: item.country,
    zip: item.zip,
    verified: item.verified,
    locked: item.locked,
  }));

  var unsubscribeGroupList = null;
  if (groups !== null && groups !== undefined) {
    unsubscribeGroupList = groups.map((item) => ({
      id: item.id,
      name: item.name,
      description: item.description
    }));
  }

  return { account: { sendgridSenders: sendersList, sendgridUnsubscribeGroups: unsubscribeGroupList }};
}

export const parseClientSendgridSenders = ({response}) => {
  const senders = response.default?.senders;
  if (!Array.isArray(senders)) {
    return invalidResponseError;
  }
 
  return {
    account: {
      sendgridClientSenders: senders.map(sender => ({
        id: sender.id,
        email: sender.from_email,
        isGlobal: sender.is_admin,
        status: sender.sender_status,
        brandId: sender.brand_id,
      }))
    }
  }
};

export const parseSendgridSender = ({response}) => {
  const defaultSender = response.default?.sender;
  const clientSender = response.client?.sender;

  const defaultAsmGroup = response.default?.unsubscribe_group;
  const clientAsmGroup = response.client?.unsubscribe_group;

  const clientWebhook = response.client?.webhook;
  
  if (defaultSender == null && clientSender == null) {
    return { account: { sendgridSender: null }};
  }

  const parseSender = (sender) => {
    return {
      id: sender.id,
      nickname: sender.nickname,
      fromEmail: sender.from_email,
      fromName: sender.from_name,
      replyTo: sender.reply_to,
      replyToName: sender.reply_to_name,
      address: sender.address,
      address2: sender.address2,
      state: sender.state,
      city: sender.city,
      country: sender.country,
      zip: sender.zip,
      verified: sender.verified,
      locked: sender.locked,
    }
  }

  const parseUnsubscribeGroup = (asmGroup) => {
    return {
      id: asmGroup.id,
      name: asmGroup.name,
      description: asmGroup.description,
    }
  }

  const parseWebhook = (webhook) => {
    return {
      id: webhook.id,
      url: webhook.url,
      name: webhook.friendly_name,
    }
  }

  const defaultSenderItem = defaultSender != null ? parseSender(defaultSender) : null;
  const clientSenderItem = clientSender != null ? parseSender(clientSender) : null;

  const defaultUnsubscribeGroupItem = (defaultAsmGroup != null) ? parseUnsubscribeGroup(defaultAsmGroup) : null;
  const clientUnsubscribeGroupItem = (clientAsmGroup != null) ? parseUnsubscribeGroup(clientAsmGroup) : null
 
  const clientWebhookItem = (clientWebhook != null) ? parseWebhook(clientWebhook) : null;

  const defaultItem = defaultSenderItem != null ? { ...defaultSenderItem, unsubscribeGroup: defaultUnsubscribeGroupItem } : null;
  const clientItem = clientSenderItem != null ? { ...clientSenderItem, unsubscribeGroup: clientUnsubscribeGroupItem, webhook: clientWebhookItem } : null;

  return { account: { sendgridSender: { default: defaultItem, client: clientItem }}};
}

export const parseClientEmailCannedMessage = ({response}) => {
  const configurations = response.configurations

  if (configurations == null || !Array.isArray(configurations)) {
    return invalidResponseError;
  }

  const configurationsList = configurations.map((item) => ({
    id: item.id,
    name: item.name,
    subject: item.subject,
    body: item.body,
    openCount: item.open_count,
    clickCount: item.click_count,
    createdAt: item.created_at,
  }));

  return { account: { emailCannedMessages: configurationsList }};
}

export const parseClientSmsCannedMessage = ({response}) => {
  const configurations = response.configurations

  if (configurations == null || !Array.isArray(configurations)) {
    return invalidResponseError;
  }

  const configurationsList = configurations.map((item) => ({
    id: item.id,
    name: item.name,
    message: item.message,
    clickCount: item.click_count,
    createdAt: item.created_at,
  }));

  return { account: { smsCannedMessages: configurationsList }};
}

const parseFilter = (filter) => {
  return filter?.map((item) => ({
    type: item.type,
    operation: item.operation,
    value: item.value
  }));
}

const parseBroadcast = (item, automation = false) => {
  if (!item) {
    return null
  }

  let emailTemplate = null;
  try {
    emailTemplate = JSON.parse(item.content?.email_template);
  } catch (error) {
    // do nothing
  }

  const automationData = automation ? {
    triggerType: item.trigger_type,
    sendDelay: item.send_delay,
    repeatInterval: item.repeat_interval,
    repeatLimit: item.repeat_limit,
  } : {};

  return {
    id: item.id,
    clientId: item.client_id,
    name: item.name,
    type: item.type,
    language: item.language,
    openCount: item.open_count,
    clickCount: item.click_count,
    lastSent: item.last_sent,
    status: item.status,
    createdAt: item.created_at,
    filter: parseFilter(item.filter),
    senderStatus: item.sender_status,
    sender: item.sendgrid_sender_info ? {
      id: item.sendgrid_sender_info.id,
      fromAddress: item.sendgrid_sender_info.from_address,
      fromName: item.sendgrid_sender_info.from_name,
      replyAddress: item.sendgrid_sender_info.reply_address,
    } : {},
    enabled: item.enabled,
    ...automationData,
    content: {
      id: item.content?.id,
      name: item.content?.name,
      subject: item.content?.subject,
      body: item.content?.body,
      emailTemplate: emailTemplate,
      message: item.content?.message,
      mmsMedia: {
        url: item.mms_image_info?.image_url,
        name: item.mms_image_info?.name,
        id: item.mms_image_info?.id,
      },
      openCount: item.content?.open_count,
      clickCount: item.content?.click_count,
      createdAt: item.content?.created_at,
      status: item.content?.status,
    }
  }
};

export const parseSendBroadcast = ({response}) => {
  return {
    temp: {
      message: response.message,
      sendStatus: response.send_status,
      lastSent: response.last_sent,
      smsClient: response.client,
    }
  }
};

export const parseMarketingBroadcasts = ({response}) => {
  const configurations = response.configurations;

  if (configurations == null || !Array.isArray(configurations)) {
    return invalidResponseError;
  }

  const configurationsList = configurations.map((item) => parseBroadcast(item));

  return {
    account: { marketingBroadcasts: configurationsList },
    temp: { marketingId: response.id }
  };
};

export const parseMarketingAutomations = ({response}) => {
  const configurations = response.configurations;

  if (configurations == null || !Array.isArray(configurations)) {
    return invalidResponseError;
  }

  const configurationsList = configurations.map((item) => parseBroadcast(item, true));

  return {
    account: { marketingCampaigns: configurationsList },
    temp: { marketingId: response.id }
  };
};

export const parseMarketingNameRecommendation = ({response}) => {
  const name = response.name;

  if (name == null) {
    return invalidResponseError;
  }

  return {
    temp: {
      marketingName: name
    }
  };
};

export const parseMarketingHomeResponse = ({response, highlightType = "", highlightId = ""}) => {
  const responseMap = [
    { key: "automation/get/all", parser: parseMarketingAutomations },
    { key: "broadcast/get/all", parser: parseMarketingBroadcasts },
    { key: "sendgrid/sender/get", parser: parseClientSendgridSenders },
    { key: "client/get", parser: parseClientResponse },
    { key: "marketing/limit/get", parser: parseMarketingDefaultLimitResponse },
    { key: "marketing/client/limit/get", parser: parseMarketingClientLimitResponse },
    { key: "marketing/client/limit/sent", parser: parseMarketingClientLimitSentResponse },
  ];

  return mergeDeep(
    {},
    ...responseMap.map(item => item.parser({ response: response[item.key] })),
    { account: { marketingHighlightType: highlightType, marketingHighlightId: highlightId } }
  );
};

const parseMarketingCampaignItem = (response) => {
  return {
    id: response.id,
    name: response.name,
    clientId: response.client_id,
    conditionType: response.condition_type,
    conditionReset: response.condition_reset,
    conditionTime: response.condition_time,
    sendImmediate: response.send_immediate === 1,
    requiredLogins: response.required_logins,
    emailCannedMessageId: response.email_canned_message_id,
    smsCannedMessageId: response.sms_canned_message_id,
    enabled: response.enabled,
    createdAt: response.created_at,
  }
}

export const parseMarketingCampaigns = ({response}) => {
  const campaigns = response.campaigns
  if (campaigns == null || !Array.isArray(campaigns)) {
    return invalidResponseError;
  }

  const campaignsList = campaigns.map((item) => (
    parseMarketingCampaignItem(item)
  ));

  return { account: { marketingCampaigns: campaignsList }};
}

export const parseMarketingCampaign = ({response}) => {
  const campaign = response.campaign

  if (campaign == null) {
    return invalidResponseError;
  }
  
  return { account: { marketingCampaign: campaign }};
}

export const parseIntegrationConfigurations = ({response}) => {
  const configurations = response.config
  if (configurations === undefined) {
    return invalidResponseError;
  } else if (configurations === null) { 
    return { account: { facebookPixel: null }};
  }

  const facebookPixelConfigurations = parseFacebookPixelConfiguration(configurations);
  const googleTagConfigurations = parseGoogleTagConfiguration(configurations);
  
  return { account: { facebookPixel: facebookPixelConfigurations, googleTag: googleTagConfigurations }};
}

export const parseFacebookPixelConfiguration = (response) => {
  const configurations = response?.facebook_pixel;
  if (configurations === undefined || configurations === null || !Array.isArray(configurations)) {
    return [];
  }

  return configurations.map((item) => ({
    id: item.id,
    clientId: item.client_id,
    code: item.code,
    mketing: item.marketing,
    login: item.login,
  }));
}

export const parseGoogleTagConfiguration = (response) => {
  const configurations = response?.google_tag;
  if (configurations === undefined || configurations === null || !Array.isArray(configurations)) {
    return [];
  }

  return configurations.map((item) => ({
    id: item.id,
    clientId: item.client_id,
    code: item.code,
    mketing: item.marketing,
    login: item.login,
  }));
}

export const parseMailchimpConfiguration = ({response}) => {
  const configuration = response.config
  const syncStatuses = response.batches;
  if (configuration === undefined) {
    return invalidResponseError;
  } else if (configuration === null) { 
    return { account: { mailchimpConfig: null, mailchimpLists: null, mailchimpSyncStatuses: null }};
  }

  const configItem = {
    id: configuration.id,
    clientId: configuration.client_id,
    name: configuration.name,
    apiKey: configuration.api_key,
    server: configuration.server,
    listId: configuration.list_id,
    listName: configuration.list_name,
    lastSyncAt: configuration.last_sync_at,
    nextSyncAt: configuration.next_sync_at,
  }

  var listsItem = null;
  if (configuration.lists !== null && configuration.lists !== undefined) {
    listsItem = configuration.lists.map((item) => ({
      id: item.id,
      name: item.name,
    }))
  }

  var syncItems = null;
  if (syncItems !== null && syncItems !== undefined) {
    syncItems = syncStatuses.map((item) => ({
      id: item.id,
      batchId: item.batch_id,
      status: item.status,
    }));
  } 
  
  return { account: { mailchimpConfig: configItem, mailchimpLists: listsItem, mailchimpSyncStatuses: syncItems }};
}


export const parseMailchimpSyncStatus = ({response}) => {
  const statuses = response.batches;
  const configuration = response.config;
  if (statuses === null || statuses === undefined || configuration === null || configuration === undefined) { 
    return invalidResponseError;
  }

  const statusItems = statuses.map((item) => ({
    id: item.id,
    batchId: item.batch_id,
    status: item.status,
  }));

  var configItem = {
    id: configuration.id,
    clientId: configuration.client_id,
    name: configuration.name,
    apiKey: configuration.api_key,
    server: configuration.server,
    listId: configuration.list_id,
    listName: configuration.list_name,
    lastSyncAt: configuration.last_sync_at,
    nextSyncAt: configuration.next_sync_at,
  }

  return { account: { mailchimpConfig: configItem, mailchimpSyncStatuses: statusItems } };
}


export const parseConstantContactConfiguration = ({response}) => {
  const configuration = response.config;
  const contactLists = response.contact_lists;
  const activities = response.activities;
  const customDomain = response.custom_domain;
  const constantContactSupported = response.has_constant_contact_config ?? true; // If not returned, not a white label client

  if (!configuration) { 
    return { 
      account: { 
        constantContactConfig: null,
        constantContactContactLists: null,
        constantContactActivities: null, 
        constantContactCustomDomain: customDomain,
        constantContactSupported: constantContactSupported,
      }
    };
  }

  const configItem = {
    id: configuration.id,
    clientId: configuration.client_id,
    listId: configuration.list_id,
    listName: configuration.list_name,
    lastSyncAt: configuration.last_sync_at,
  }

  var result = { constantContactConfig: configItem, constantContactCustomDomain: customDomain, constantContactSupported: constantContactSupported };

  var listsItem = null;
  if (contactLists !== null && contactLists !== undefined) {
    listsItem = contactLists.map((item) => ({
      listId: item.list_id,
      listName: item.name,
      membershipCount: item.membership_count,
    }))

    result = { ...result, constantContactContactLists: listsItem };
  }

  var activitiesItem = null;
  if (activities !== null && activities !== undefined) {
    activitiesItem = activities.map((item) => ({
      id: item.id,
      activityId: item.activity_id,
      clientId: item.client_id,
      state: item.state, 
    }))

    result = { ...result, constantContactActivities: activitiesItem };
  }

  return { account: { ...result }};
}


export const parseConstantContactSyncStatus = ({response}) => {
  const activities = response.activities;
  const configuration = response.config;
  if (activities === null || activities === undefined) { 
    return invalidResponseError;
  }

  const configItem = {
    id: configuration.id,
    clientId: configuration.client_id,
    listId: configuration.list_id,
    listName: configuration.list_name,
    lastSyncAt: configuration.last_sync_at,
  }
 

  var activitiesItem = activities.map((item) => ({
    id: item.id,
    activityId: item.activity_id,
    clientId: item.client_id,
    state: item.state, 
  }));

  return { account: { constantContactConfig: configItem, constantContactActivities: activitiesItem } };
}

export const parseZapierConfiguration = ({response}) => {
  const configuration = response.config

  if (configuration === undefined) {
    return invalidResponseError;
  } else if (configuration === null) { 
    return { account: { zapierConfig: null }};
  }

  const parseWebhooks = (webhooks) => {
    return webhooks?.map((item, index) => ({
      id: item.id,
      clientId: item.client_id,
      api_key: item.api_key,
      type: item.type,
      webhookUrl: item.webhook_url,
      lastUpdatedAt: item.last_updated_at,
    }));
  }

  const configItem = {
    id: configuration.id,
    clientId: configuration.client_id,
    apiKey: configuration.api_key,
    appLink: configuration.app_link,
    webhooks: parseWebhooks(configuration.webhooks),
  }

  return { account: { zapierConfig: configItem }};
}

export const parseZapierCrms = ({response}) => {
  const crms = response.top_crms;
  if (crms == null || !Array.isArray(crms)) {
    return invalidResponseError;
  }

  const crmItems = crms.map((item) => ({
    title: item.title,
    description: item.description,
    imageUrl: item.image_url,
  }));

  return { account: { zapierCrms: crmItems }}
}

export const parseReviewAutomationConfig = ({response}) => {
  const configuration = response.configuration

  if (configuration === undefined) {
    return invalidResponseError;
  } else if (configuration === null) { 
    return { account: { reviewAutomationConfig: null }};
  }


  const configItem = {
    id: configuration.id,
    clientId: configuration.client_id,
    enabled: configuration.enabled
  }
  
  const pagesItem = configuration.pages?.map((item) => ({
    id: item.id,
    clientId: item.client_id,
    locationId: item.location_id,
    configId: item.config_id,
    type: item.type,
    enabled: item.enabled,
    reviewUrl: item.review_url,
    reviewSearch: item.review_search,
    dataId: item.data_id,
    placeId: item.place_id,
    lastUpdateCheck: item.last_update_check,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
  }));

  return { account: { reviewAutomationConfig: configItem , reviewAutomationPages: pagesItem }};
}

export const parseReviewAutomationPageConfigs = ({response}) => {
  const pages = response.pages

  if (pages === undefined) {
    return invalidResponseError;
  } else if (pages === null) { 
    return { account: { reviewAutomationPages: null }};
  }


  const pagesItem = pages?.map((item) => ({
    id: item.id,
    clientId: item.client_id,
    locationId: item.location_id,
    configId: item.config_id,
    type: item.type,
    enabled: item.enabled,
    reviewUrl: item.review_url,
    reviewSearch: item.review_search,
    dataId: item.data_id,
    placeId: item.place_id,
    lastUpdateCheck: item.last_update_check,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
  }));

  return { account: { reviewAutomationPages: pagesItem }};
}

export const parseReviewAutomationReviews = ({response}) => {
  const reviews = response.reviews;
  if (reviews === undefined) {
    return invalidResponseError;
  } else if (reviews === null) { 
    return { account: { reviewAutomationReviews: null }};
  }

  const reviewsItem = reviews?.map((item) => ({
    id: item.id,
    clientId: item.client_id,
    locationId: item.locationId,
    configId: item.config_id,
    pageId: item.page_id,
    guestId: item.guest_id,
    guestName: item.guest_name, 
    reviewDate: item.review_date,
    reviewDateTimestamp: item.review_date_timestamp,
    reviewId: item.review_id,
    reviewLinkUrl: item.review_link_url,
    reviewRating: item.review_rating,
    snippet: item.snippet,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
    reviewerName: item.user_name,
    reviewerLink: item.user_link_url,
  }))

  return { account: { reviewAutomationReviews: reviewsItem }};
}

export const parseReviewAutomationPageReviewUpdate = ({response}) => {
  const pages = response.pages
  const reviews = response.reviews;
  if (pages === undefined) {
    return invalidResponseError;
  } else if (pages === null) { 
    return { account: { reviewAutomationPages: null }};
  }


  const pagesItem = pages?.map((item) => ({
    id: item.id,
    clientId: item.client_id,
    locationId: item.location_id,
    configId: item.config_id,
    type: item.type,
    enabled: item.enabled,
    reviewUrl: item.review_url,
    reviewSearch: item.review_search,
    dataId: item.data_id,
    placeId: item.place_id,
    lastUpdateCheck: item.last_update_check,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
  }));

  const reviewsItem = reviews?.map((item) => ({
    id: item.id,
    clientId: item.client_id,
    locationId: item.locationId,
    configId: item.config_id,
    pageId: item.page_id,
    reviewDate: item.review_date,
    reviewDateTimestamp: item.review_date_timestamp,
    reviewId: item.review_id,
    reviewLinkUrl: item.review_link_url,
    reviewRating: item.review_rating,
    snippet: item.snippet,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
    reviewerName: item.user_name,
    reviewerLink: item.user_link_url,
  }))

  return { account: { reviewAutomationPages: pagesItem, reviewAutomationReviews: reviewsItem }};
}

export const parseReviewSearchVerify = ({response}) => {
  return { account: { verifiedReviewSearch: response.review_search ?? null }};
}

export const parseAllMarketingBroadcasts = ({response}) => {
  const emailBroadcasts = response.email_broadcasts;
  const smsBroadcasts = response.sms_broadcasts;

  if (!Array.isArray(emailBroadcasts) || !Array.isArray(smsBroadcasts)) {
    return invalidResponse;
  }

  return {
    marketing: {
      broadcasts: [ ...emailBroadcasts, ...smsBroadcasts ].map(broadcast => ({
        id: broadcast.id,
        name: broadcast.name,
        subject: broadcast.subject,
        body: broadcast.body,
        message: broadcast.message,
        openCount: broadcast.open_count,
        clickCount: broadcast.click_count,
        createdAt: broadcast.created_at
      }))
    }
  };
};

export const parseLocationAddressValid = ({response}) => {
  return {
    provisioning: {
      googleReview: {
        missing: response.missing,
        unconfirmed: response.unconfirmed,
        replaced: response.replaced,
        search: response.google_review_search,
        dataId: response.google_data_id,
        placeId: response.google_place_id,
      }
    },
    temp: {
      suggestedAddress: {
        street: response.street,
        city: response.city,
        state: response.state,
        zip: response.zip,
        country: response.country,
      }
    }
  };
};

const parseMarketingTemplate = (item) => {
  const isAutomation = item.type === "automation";
  return {
    id: item.id,
    title: item.title,
    description: item.description,
    icon: item.icon,
    type: item.type,
    configId: item.config_id,
    config: parseBroadcast(item.config, isAutomation),
  };
};

export const parseMarketingTemplateResponse = ({response}) => {
  const template = response.template;
  if (!template) {
    return invalidResponseError;
  }

  return {
    account: {
      marketingTemplate: parseMarketingTemplate(template)
    }
  };
};

export const parseMarketingAllTemplateResponse = ({response}) => {
  const templates = response.template_list;
  if (!Array.isArray(templates)) {
    return invalidResponseError;
  }

  return {
    account: {
      marketingTemplates: templates.map(template => parseMarketingTemplate(template))
    }
  };
};

export const parseMarketingDefaultLimitResponse = ({response}) => {
  const marketingLimits = response.marketing_limits;
  if (marketingLimits == null || !marketingLimits.id) {
    return invalidResponseError;
  }

  return {
    account: {
      marketingLimits: {
        broadcastSendLimit: marketingLimits.broadcast_send_limit,
        broadcastSmsLimit: marketingLimits.broadcast_sms_limit,
        broadcastEmailLimit: marketingLimits.broadcast_email_limit,
        automationSmsLimit: marketingLimits.automation_sms_limit,
        automationEmailLimit: marketingLimits.automation_email_limit,
      }
    }
  }
}

export const parseMarketingClientLimitResponse = ({response}) => {
  const marketingLimits = response.marketing_limits;
  if (marketingLimits == null) {
    return invalidResponseError;
  }

  // If no "id", client limit not set
  if (!marketingLimits.id) {
    return {
      account: {
        marketingClientLimits: null
      }
    }
  }

  return {
    account: {
      marketingClientLimits: {
        clientId: marketingLimits.client_id,
        broadcastSendLimit: marketingLimits.broadcast_send_limit,
        broadcastSmsLimit: marketingLimits.broadcast_sms_limit,
        broadcastEmailLimit: marketingLimits.broadcast_email_limit,
        automationSmsLimit: marketingLimits.automation_sms_limit,
        automationEmailLimit: marketingLimits.automation_email_limit,
      }
    }
  }
};

export const parseMarketingClientLimitSentResponse = ({response}) => {  
  const marketingLimits = response.marketing_limits;
  if (marketingLimits == null) {
    return invalidResponseError;
  }

  return {
    account: {
      marketingClientLimitsSent: {
        clientId: marketingLimits.client_id,
        broadcastSendLimitEmail: marketingLimits.broadcast_send_limit_email,
        broadcastSendLimitSms: marketingLimits.broadcast_send_limit_sms,
        broadcastSmsLimit: marketingLimits.broadcast_sms_limit,
        broadcastEmailLimit: marketingLimits.broadcast_email_limit,
        automationSmsLimit: marketingLimits.automation_sms_limit,
        automationEmailLimit: marketingLimits.automation_email_limit,
      }
    }
  }
};

export const parseMarketingTemplateNameResponse = ({response}) => {
  const name = response.name;
  if (!name) {
    return invalidResponseError;
  }

  return {
    account: {
      marketingTemplateName: name
    }
  };
};

export const parseAIGeneratedImage = ({response}) => {
  const images = response.images?.[0];
  if (!images) {
    return invalidResponseError;
  }

  return {
    temp: {
      imageUrl: images.image_url,
      pendingImageUrl: images.pending_image_url,
    }
  }
};

export const parseProductAndServices = ({response}) => {
  const productAndServices = response.product_and_services;
  if (!productAndServices) {
    return invalidResponseError;
  }

  return {
    account: {
      marketingProductAndServices: productAndServices
    }
  };
};

export const parsePayPalBillingSubscriptionsRevise = ({response}) => {
  const links = response.links;
  if (!links) {
    return invalidResponseError;
  }

  const linkItems = links.map((item) => ({ url: item.url, action: item.action, method: item.method }));
  return {
    account: {
      billing: {
        links: linkItems
      }
    }
  };
};

export const parsePaypalPlan = ({response}) => {
  const plan = response.plan;
  const billingCycles = plan?.billing_cycles;
  const paymentPreferences = plan?.payment_preferences;

  if (!Array.isArray(billingCycles) || billingCycles.length === 0 || paymentPreferences == null) {
    return invalidResponseError;
  }
  
  const planInfo = { name: plan.name, count: 1, id: plan.id, description: plan.description };
  billingCycles.forEach(cycle => {
    if (cycle.tenure_type === "TRIAL") {
      // record trial info
      planInfo.trial = {
        intervalUnit: cycle.frequency.interval_unit.toLowerCase(),
        intervalCount: cycle.frequency.interval_count,
      }
    } else {
      // record price cycle
      planInfo.currency = cycle.price.currency_code;
      planInfo.price = cycle.price.price;
      planInfo.intervalUnit = cycle.frequency.interval_unit.toLowerCase();
      planInfo.intervalCount = cycle.frequency.interval_count;
    }
  });

  planInfo.setupFee = paymentPreferences.setup_fee_value;

  return {
    account: {
      billing: { plan: planInfo }
    }
  };
};

export const parsePaypalPlans = ({response}) => {
  const plans = response.plans;
  if (!Array.isArray(plans) || plans.length === 0) {
    return invalidResponseError;
  }

  const planItems = plans.map((item) => ({ name: item.name, id: item.id, description: item.description }));

  return {
    account: {
      billing: { plans: planItems }
    }
  };
};


export const parsePaypalDiscount = ({response}) => {
  if (!response.discount) { 
    return { error: { message: response.message ?? "Error"} };
  }

  const { id, enabled, currency_code, code, discount_type, discount_value } = response.discount;
  return {
    account: {
      discountCode: {
        id: id, 
        enabled: enabled, 
        currency: currency_code,
        discountCode: code,
        discountType: discount_type,
        discountValue: discount_value,
      }
    }
  }
};

export const parsePaypalDiscounts = ({response}) => {
  const discounts = response.discounts;
  if (discounts == null || !Array.isArray(discounts) ) { 
    return invalidResponseError;
  }

  let discountItems = discounts.map((item) => {
    const { id, enabled, currency_code, code, discount_type, discount_value } = item;
    return {
      id: id,
      enabled: enabled,
      currency: currency_code,
      discountCode: code,
      discountType: discount_type,
      discountValue: discount_value
    }
  })

  return {
    account: {
      discountCodes: discountItems
    }
  }
}

export const parseBrandingList = ({response}) => {
  const branding = response.branding;
  if (branding == null || !Array.isArray(branding) ) { 
    return invalidResponseError;
  }

  let brandingItems = branding.map((item) => {
    return parseBrandItem(item);
  })

  return {
    account: {
      brands: brandingItems
    }
  }
} 

export const parseBranding = ({domainBrand=false, response}) => {
  const branding = response.branding;
  if (branding == null) { 
    return invalidResponseError;
  }

  let brandItem = parseBrandItem(branding);
  return {
    account: {
      brand: brandItem,
      ...(domainBrand) && { domainBrand: brandItem },
    }
  }
}

const parseBrandItem = (item) => {
  return {
    id: item.id,
    accountId: item.account_id,
    title: item.title,
    address: item.address,
    email: item.email,
    locationLimit: item.location_limit,
    locationCount: item.location_count,
    brandingStatus: item.branding_status,
    brandingError: item.branding_error,
    configurationComplete: item.configuration_complete,
    dashboardDomain: item.dashboard_domain,
    dashboardDomainVerified: item.dashboard_domain_verified,
    dashboardDomainEnabled: item.dashboard_domain_enabled,
    dashboardDomainError: item.dashboard_domain_error,
    logoImageId: item.logo_image_id,
    faviconImageId: item.favicon_image_id,
    hasSmsConfig: item.has_sms_config,
    hasEmailConfig: item.has_email_config,
    hasFacebookConfig: item.has_facebook_config,
    hasGoogleConfig: item.has_google_config,
    hasTwitterConfig: item.has_twitter_config,
    hasConstantContactConfig: item.has_constant_contact_config,
    termsOfServiceUrl: item.terms_of_service_url,
    termsOfServiceEnabled: item.terms_of_service_enabled,
    privacyPolicyUrl: item.privacy_policy_url,
    privacyPolicyEnabled: item.privacy_policy_enabled,
    accounts: item.accounts?.map((account) => ({
      id: account.id,
      username: account.username
    })),
    logo: item.logo ? {
      id: item.logo.id,
      imageUrl: item.logo.image_url,
      size: item.logo.size,
    } : null,
    favicon: item.favicon ? {
      id: item.favicon.id,
      imageUrl: item.favicon.image_url,
      size: item.favicon.size,
    } : null,
    sendgridConfig: item.sendgrid_config ? {
      apiKey: item.sendgrid_config.api_key,
      fromAddress: item.sendgrid_config.from_address,
      replyAddress: item.sendgrid_config.reply_address,
      senderId: item.sendgrid_config.sender_id,
      status: item.sendgrid_config.status,
      error: item.sendgrid_config.error,
      footerHtml: item.sendgrid_config.footer_html,
      unsubscribeEnabled: item.sendgrid_config.unsubscribe_enabled,
      webhookStatus: item.sendgrid_config.webhook_status,
      webhookError: item.sendgrid_config.webhook_error
    } : null, 
    paypalConfig: item.paypal_config ? {
      clientId: item.paypal_config.client_id,
      clientSecret: item.paypal_config.client_secret,
      status: item.paypal_config.status,
      error: item.paypal_config.error
    } : null, 
    twilioSmsConfig: item.twilio_sms_config ? {
      phoneNumber: item.twilio_sms_config.phone_number,
      accountSid: item.twilio_sms_config.account_sid,
      authToken: item.twilio_sms_config.auth_token,
      status: item.twilio_sms_config.status,
      error: item.twilio_sms_config.error,
      enabled: item.twilio_sms_config.enabled,
    } : null, 
    plivoSmsConfig: item.plivo_sms_config ? {
      phoneNumber: item.plivo_sms_config.phone_number,
      authId: item.plivo_sms_config.auth_id,
      authToken: item.plivo_sms_config.auth_token,
      status: item.plivo_sms_config.status,
      error: item.plivo_sms_config.error,
      enabled: item.plivo_sms_config.enabled,
    } : null, 
    turboSmsConfig: item.turbo_sms_config ? {
      phoneNumber: item.turbo_sms_config.phone_number,
      authToken: item.turbo_sms_config.auth_token,
      status: item.turbo_sms_config.status,
      error: item.turbo_sms_config.error,
      enabled: item.turbo_sms_config.enabled,
    } : null,
    facebookConfig: item.facebook_config ? {
      clientId: item.facebook_config.client_id,
      clientSecret: item.facebook_config.client_secret,
      status: item.facebook_config.status,
      error: item.facebook_config.error,
      enabled: item.facebook_config.enabled,
    } : null,
    googleConfig: item.google_config ? {
      clientId: item.google_config.client_id,
      clientSecret: item.google_config.client_secret,
      status: item.google_config.status,
      error: item.google_config.error,
      enabled: item.google_config.enabled,
    } : null,
    twitterConfig: item.twitter_config ? {
      consumerKey: item.twitter_config.consumer_key,
      consumerSecret: item.twitter_config.consumer_secret,
      status: item.twitter_config.status,
      error: item.twitter_config.error,
      enabled: item.twitter_config.enabled,
    } : null,
    constantContactConfig: item.constant_contact_config ? {
      apiKey: item.constant_contact_config.api_key,
      appSecret: item.constant_contact_config.app_secret,
      status: item.constant_contact_config.status,
      error: item.constant_contact_config.error,
      enabled: item.constant_contact_config.enabled,
    } : null,
    readOnlyAutoLogins: item.read_only_auto_logins,
    clients: item.clients,
    isReadOnly: item.is_read_only,
  }
}

export const parseGdprRequiredResponse = ({response}) => {
  const { force_gdpr } = response;
  return { temp: { forceGdpr: force_gdpr ?? false }};
};

export const parseErrorMessage = ({response}) => {
  return { error: { message: response.message }};
}

export const parseAllMarketingAutomations = ({response}) => {
  // TODO
};

export const parseSendgridInfoResponse = ({response}) => {
  // TODO
};

export const parseTwilioInfoResponse = ({response}) => {
  // TODO
};

export const parseMailchimpInfoResponse = ({response}) => {
  // TODO
};

export const parseZapierInfoResponse = ({response}) => {
  // TODO
};
